// frontend/src/createApp.ts
import { createApp as vueCreateApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import './assets/global.css';
import './assets/design-tokens.css';
import { configureAmplify } from './amplifyConfig';
import {
  registerCoreModules,
  registerTenantModules,
  configureTenant,
  type ModuleConfig,
  type TenantConfig,
  type SidebarItem,
} from './config/moduleRegistry';
import { coreModuleDefinitions } from './stores/appStore';
import { createAppRouter } from './router';
import { initUsageTracker } from './usage';
import { installSessionExpiryWatch } from './utils/sessionExpiry';
import { setTenantUi, type TenantUiPack } from './tenant/tenantUi';

export interface TenantAppConfig {
  /** Tenant identifier, used for branding (logo path, favicon). */
  tenantId: string;
  /** Full tenant configuration (modules, sidebar, account). */
  config: TenantConfig;
  /** Tenant-specific custom modules to register alongside core modules. */
  customModules: ModuleConfig[];
  /** Tenant UI pack: terminology, extra fields, default views (optional —
   *  omitting it renders Core's neutral defaults, i.e. today's wording). */
  tenant?: TenantUiPack;
}

/**
 * Collect every module ID referenced in the sidebar array.
 */
function collectSidebarModuleIds(sidebar: SidebarItem[]): string[] {
  const ids: string[] = [];
  for (const item of sidebar) {
    if (item.type === 'module') {
      ids.push(item.id);
    } else if (item.type === 'category') {
      ids.push(...item.children);
    }
  }
  return ids;
}

/**
 * Validate tenant app configuration. Throws with actionable messages.
 */
function validateAppConfig(config: TenantAppConfig): void {
  const errors: string[] = [];

  if (!config.tenantId) {
    errors.push("missing required field 'tenantId'");
  }
  if (!config.config || typeof config.config !== 'object') {
    errors.push("missing required field 'config' (must be TenantConfig)");
  }
  if (!Array.isArray(config.customModules)) {
    errors.push("missing required field 'customModules' (must be ModuleConfig[])");
  }

  // Validate config sub-fields
  const tc = config.config;
  if (tc) {
    if (!tc.modules || typeof tc.modules !== 'object') {
      errors.push("config.modules must be Record<string, boolean>");
    }
    if (!Array.isArray(tc.sidebar)) {
      errors.push("config.sidebar must be SidebarItem[]");
    }
    if (!tc.account || typeof tc.account !== 'object') {
      errors.push("config.account must be AccountConfig");
    }
  }

  // Bail early on structural errors
  if (errors.length > 0) {
    throw new Error(`createApp config validation failed:\n  - ${errors.join('\n  - ')}`);
  }

  // --- Semantic validation ---

  const knownIds = new Set([
    ...coreModuleDefinitions.map(m => m.id),
    ...(config.customModules || []).map(m => m.id),
  ]);

  // Check for unknown module IDs in config.modules
  for (const id of Object.keys(tc.modules)) {
    if (!knownIds.has(id)) {
      const closest = [...knownIds].find(k =>
        k.toLowerCase().includes(id.toLowerCase()) ||
        id.toLowerCase().includes(k.toLowerCase())
      );
      const hint = closest ? ` — did you mean '${closest}'?` : '';
      errors.push(`unknown module '${id}' in config.modules${hint}`);
    }
  }

  // Check for unknown module IDs in sidebar references
  const sidebarIds = collectSidebarModuleIds(tc.sidebar);
  for (const id of sidebarIds) {
    if (!knownIds.has(id)) {
      const closest = [...knownIds].find(k =>
        k.toLowerCase().includes(id.toLowerCase()) ||
        id.toLowerCase().includes(k.toLowerCase())
      );
      const hint = closest ? ` — did you mean '${closest}'?` : '';
      errors.push(`unknown module '${id}' in sidebar config${hint}`);
    }
  }

  // Check for unknown module IDs in account.children
  for (const id of tc.account.children) {
    if (!knownIds.has(id)) {
      const closest = [...knownIds].find(k =>
        k.toLowerCase().includes(id.toLowerCase()) ||
        id.toLowerCase().includes(k.toLowerCase())
      );
      const hint = closest ? ` — did you mean '${closest}'?` : '';
      errors.push(`unknown module '${id}' in account.children${hint}`);
    }
  }

  // Detect duplicate module placement — a module should appear at most once
  // across sidebar items and account.children
  const placements = new Map<string, string[]>();

  for (const item of tc.sidebar) {
    if (item.type === 'module') {
      const list = placements.get(item.id) || [];
      list.push(`sidebar (standalone)`);
      placements.set(item.id, list);
    } else if (item.type === 'category') {
      for (const childId of item.children) {
        const list = placements.get(childId) || [];
        list.push(`sidebar category '${item.id}'`);
        placements.set(childId, list);
      }
    }
  }

  for (const childId of tc.account.children) {
    const list = placements.get(childId) || [];
    list.push('account.children');
    placements.set(childId, list);
  }

  for (const [moduleId, places] of placements) {
    if (places.length > 1) {
      errors.push(
        `module '${moduleId}' placed in multiple locations: ${places.join(', ')}`
      );
    }
  }

  if (errors.length > 0) {
    throw new Error(`createApp config validation failed:\n  - ${errors.join('\n  - ')}`);
  }
}

/**
 * Create and mount the Vue application with tenant configuration.
 */
export async function createApp(config: TenantAppConfig): Promise<void> {
  validateAppConfig(config);

  // 0. Install the tenant UI pack before anything renders.
  setTenantUi(config.tenant);

  // 1. Register core modules
  registerCoreModules(coreModuleDefinitions);

  // 2. Register tenant custom modules
  if (config.customModules.length > 0) {
    registerTenantModules(config.customModules);
  }

  // 3. Apply tenant configuration (module ordering, sidebar layout, account menu)
  configureTenant(config.config);

  // 4. Configure AWS Amplify (reads from VITE_* env vars)
  configureAmplify();

  // 5. Create and mount Vue app
  const app = vueCreateApp(App);
  const pinia = createPinia();
  app.use(pinia);

  // Router must be created AFTER pinia (the permission guard reads from a Pinia store)
  const router = createAppRouter();
  app.use(router);

  // Usage analytics: page views + time-on-page for every module (custom
  // modules included — the hook reads route meta). Silent-failure contract.
  try { initUsageTracker(router); } catch { /* tracking must never break boot */ }

  // Hold first paint until the initial route AND its module's async component
  // chunk are loaded, so the shell (sidebar) and the module's own chrome
  // appear in the same frame — otherwise the module header blinks in after
  // the sidebar. Route components are defineAsyncComponent wrappers, which
  // router.isReady() does not wait for; __asyncLoader is Vue's memoized
  // chunk loader on such wrappers (absent → harmless no-op).
  //
  // isReady() REJECTS when the initial navigation aborts — which is the
  // normal logged-out path (the permission guard finds no permitted modules
  // and returns false). Mount must proceed anyway or App.vue's login gate
  // never renders and every tenant boots to a blank page.
  await router.isReady().catch(() => undefined);
  await Promise.all(
    router.currentRoute.value.matched
      .flatMap((record) => Object.values(record.components ?? {}))
      .map((c) => (c as any)?.__asyncLoader?.().catch(() => undefined))
  );

  // A 401 on a token-less /api call means the browser session lapsed: App.vue re-authenticates.
  installSessionExpiryWatch();
  app.mount('#app');
}
